/**
 * SceneViewerScreen — Scenes-tab image viewer.
 *
 * #169 Phase 1a: this screen is now a thin DATA-LOADER wrapper. All
 * presentation moved into the shared `ScenePhotoView` component so the
 * scenes-tab viewer and (Phase 1b) the post-scan review render through
 * one code path — no more "two kinds of scene page."
 *
 * Responsibilities kept here:
 *   - resolve `fileId` from the route
 *   - populate `scenesCache` when the scene isn't already loaded
 *     (cold deep-link / deep-in-the-list cases)
 *   - loading state
 * Everything visual (stage + bbox overlays + item list + group action)
 * lives in ScenePhotoView.
 *
 * Data source: `state.browse.scenesCache.scenes` (FE-9.4.1's cache via
 * `loadScenesPage`), with a single-scene fallback fetch.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';

import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { loadScenesPage, loadSceneById, selectScenes } from './browseSlice';
import { ScenePhotoView } from './components/ScenePhotoView';
import { DeleteSceneSheet } from './components/DeleteSceneSheet';
import type { InventoryItemResponse } from '../../api/types';
import { api } from '../../api/remembrApi';
import type { Bbox } from '../segment/useImageTransform';

interface SceneViewerRouteParams {
  /** FileRecord id — keyed against `scenesCache.scenes[].id`. */
  fileId: number | string;
}

export const SceneViewerScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const dispatch = useAppDispatch();

  const params = (route.params ?? {}) as SceneViewerRouteParams;
  const fileId = Number(params.fileId);

  const scenes = useAppSelector(selectScenes);
  const scene = useMemo(
    () => scenes.find((s) => s.id === fileId),
    [scenes, fileId],
  );

  // Critical story — delete the whole scan / source from the viewer.
  // Reuses DeleteSceneSheet (cascade choice + deleteScene thunk); on
  // success we leave the now-deleted scene.
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Populate the cache when the requested scene isn't in it:
  //   (a) cache cold (deep-link / refresh) → load page 1
  //   (b) cache warm but this scene is past page 1 → single-scene fetch
  useEffect(() => {
    if (!Number.isFinite(fileId)) return;
    if (scene) return;
    if (scenes.length === 0) {
      dispatch(loadScenesPage({ mode: 'replace' }) as any);
    }
    dispatch(loadSceneById({ fileId }) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const handleBack = useCallback(() => goBackOrHome(navigation), [navigation]);

  const handleItemPress = useCallback(
    (item: InventoryItemResponse) => {
      const id = (item as any)?.id;
      navigation.navigate('ItemDetail', {
        itemId: id != null ? String(id) : undefined,
        item,
      });
    },
    [navigation],
  );

  if (!scene) {
    return (
      <View style={styles.page}>
        <View style={styles.topBar}>
          <Pressable
            onPress={handleBack}
            accessibilityRole="button"
            accessibilityLabel="Back"
            hitSlop={10}
            style={styles.backBtn}
            testID="scene-photo-back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            Snapshot
          </Text>
          <View style={{ width: 32 }} />
        </View>
        <View style={styles.emptyWrap}>
          <ActivityIndicator size="small" color={fpColors.primary} />
          <Text style={styles.emptyText}>Loading snapshot…</Text>
        </View>
      </View>
    );
  }

  const itemCount = scene.inventory_items?.length ?? 0;
  const dateLabel = formatSceneDate(scene.created_at);
  const subtitle = `${dateLabel ? `${dateLabel} · ` : ''}${
    itemCount === 1 ? '1 item' : `${itemCount} items`
  }`;

  return (
    <>
      <ScenePhotoView
        mode="saved-scene"
        imageUrl={scene.storage_url ?? null}
        items={(scene.inventory_items ?? []) as InventoryItemResponse[]}
        title={scene.filename || `Snapshot ${scene.id}`}
        subtitle={subtitle}
        aiDescription={scene.ai_description ?? null}
        onBack={handleBack}
        onItemPress={handleItemPress}
        onSharesChanged={() => dispatch(loadSceneById({ fileId }) as any)}
        // AI tap-add on a saved scene (BE #411). Previously this fired a
        // navigation to `Upload` with resume=true, which bounced right
        // back to SceneViewer because SegmentScreen auto-redirects
        // COMPLETED scans. Now it runs in-place: the FE manufactures a
        // bbox, POSTs `/inventory/manual-analyze`, the BE returns a
        // fully-filled inventory row, and we re-fetch the scene to
        // pick it up.
        tapAddAnalyze={(
          bbox: Bbox,
          meta: {
            draw_duration_ms: number;
            device_kind: 'mouse' | 'touch' | 'pen';
          },
        ) =>
          api.manualAnalyze({
            file_record_id: fileId,
            bbox,
            interaction: {
              device_kind: meta.device_kind,
              draw_duration_ms: meta.draw_duration_ms,
            },
          })
        }
        onItemAdded={() => {
          // Refresh the scene so the new item + its bbox show up in
          // both the rail and the bbox overlay layer. Soft-fail
          // placeholders still get refreshed — the user can rename
          // them inline from the card via the existing edit flow.
          dispatch(loadSceneById({ fileId }) as any);
        }}
        onDelete={() => setDeleteOpen(true)}
      />
      <DeleteSceneSheet
        visible={deleteOpen}
        scene={scene}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          goBackOrHome(navigation);
        }}
      />
    </>
  );
};

/* ── helpers ──────────────────────────────────────────────────── */

function formatSceneDate(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/* ── styles (loading state only — rest lives in ScenePhotoView) ── */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  backBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { fontSize: 16, fontWeight: '700', color: fpColors.onSurface },
  emptyWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyText: { fontSize: 13, color: fpColors.onSurfaceVariant },
});

export default SceneViewerScreen;
